// What the dispatch board says beside and under its lanes, in words (BL-126, BL-127): each
// technician's clock under their name, and the tabs of jobs under the board (dispatch.lists). Pure, so
// it is tested without a browser; times are the company's, whatever the browser's zone.

import { dateText, localDateOf, sentences, timeText } from '@flowology/kernel';
import { minutesText } from './books/timesheet-model.ts';
import { pausedSince, pauseWords, windowText, type BoardVisit, type TechDay } from './board-model.ts';
import { shownText } from './shared/pages.ts';
import { pagePath } from './blueprint/places.ts';

/** A technician's clock-in and clock-out on the board's day (dispatch.board's `clock`). */
export interface TechClock {
  in: string | null;
  out: string | null;
}

/**
 * BL-126: the line under a technician's name. Today: "Not clocked in", "On the clock 4 h 10 min",
 * "On a break" (on unpaid non-job time, such as lunch) or "Clocked out 4:30 p.m."; an earlier day:
 * "Clocked in 7:42 a.m." or "Didn't clock in". Nothing for a later day, or from a server without it.
 * `title` says it all: when they clocked in and out, and how long.
 */
export function clockLine(
  c: TechClock | undefined,
  day: Pick<TechDay, 'blocks'> | undefined,
  o: {
    date: string;
    today: string;
    now: string;
    timeZone: string;
    /** QA-694: their visits today not yet started; clocked out with some, the line says so. */
    booked?: number;
  },
): { text: string; title: string; warn?: boolean } | null {
  if (!c || o.date > o.today) return null;
  const today = o.date === o.today;
  const at = (t: string) => timeText(t, o.timeZone);
  if (!c.in) {
    const text = today ? 'Not clocked in' : 'Didn’t clock in';
    return { text, title: `${text}.` };
  }
  const span = (to: string) =>
    minutesText(Math.max(0, Math.floor((Date.parse(to) - Date.parse(c.in!)) / 60_000)));
  // a clock-in from an earlier day never clocked out (QA-764): flagged, with its day
  const inDay = localDateOf(c.in, o.timeZone);
  if (!c.out && inDay < o.date)
    return {
      text: `Clocked in ${dateText(inDay, 'UTC', { year: false, weekday: 'short' })}, no clock-out`,
      title: sentences(
        `Clocked in ${dateText(inDay, 'UTC', { year: false, weekday: 'short' })} at ${at(c.in)} and never clocked out`,
        'Fix their hours',
      ),
      warn: true,
    };
  if (c.out && today && o.booked) {
    const left = stillBookedText(o.booked);
    const it = o.booked === 1 ? 'it' : 'them';
    return {
      text: `Clocked out ${at(c.out)}, ${left}`,
      title: sentences(
        `Clocked in ${at(c.in)}, out ${at(c.out)}: ${span(c.out)} on the clock`,
        `${left} today: rebook ${it} or give ${it} to someone else`,
      ),
      warn: true,
    };
  }
  if (c.out)
    return {
      text: today ? `Clocked out ${at(c.out)}` : `Clocked in ${at(c.in)}`,
      title: `Clocked in ${at(c.in)}, out ${at(c.out)}: ${span(c.out)} on the clock.`,
    };
  // a day gone by, clocked in and never out (QA-764): flagged, not said as if they were still on
  if (!today)
    return {
      text: `Clocked in ${at(c.in)}, no clock-out`,
      title: sentences(`Clocked in ${at(c.in)} and never clocked out`, 'Fix their hours'),
      warn: true,
    };
  const rest = day?.blocks.find((b) => b.kind === 'non_job' && b.live && b.paid === false);
  if (rest)
    return {
      text: 'On a break',
      title: sentences(`On a break (${rest.label}) since ${at(rest.start)}`, `Clocked in ${at(c.in)}`),
    };
  return {
    text: `On the clock ${span(o.now)}`,
    title: `Clocked in ${at(c.in)}: ${span(o.now)} on the clock.`,
  };
}

/** QA-694: "1 visit still booked", "2 visits still booked". */
export const stillBookedText = (n: number) => `${n} ${n === 1 ? 'visit' : 'visits'} still booked`;

/**
 * QA-694: a technician's visits on `date` that they haven't started: booked to them, or sent and not
 * set off. What is left with them when they end the day, for the office to rebook.
 */
export function notStartedFor(
  visits: readonly Pick<BoardVisit, 'start' | 'status' | 'techs'>[],
  techId: string,
  date: string,
  timeZone: string,
): number {
  return visits.filter((v) => {
    if (localDateOf(v.start, timeZone) !== date) return false;
    const own = v.techs?.find((x) => x.id === techId)?.fieldStatus;
    return own
      ? own === 'assigned' || own === 'dispatched'
      : v.status === 'scheduled' || v.status === 'dispatched';
  }).length;
}

// ── BL-127: the tabs under the board ──

export const LIST_TABS = [
  // QA-850: an earlier day's visits nobody reached, so the morning after doesn't drop them
  ['missed', 'Missed'],
  ['onHold', 'On hold'],
  ['inProgress', 'In progress (WIP)'],
  ['future', 'Future'],
  ['cancelled', 'Cancelled'],
  ['unscheduled', 'Unscheduled'],
] as const;
export type ListTab = (typeof LIST_TABS)[number][0];

/** A job on the Cancelled or Unscheduled tab (dispatch.lists' `jobs`). */
export interface ListJob {
  jobId: string;
  jobNumber: string;
  status: string;
  summary: string;
  customerName: string;
  address: string;
  bookedAt: string;
  lastVisitAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
}

/** dispatch.lists' answer: every tab's count, and one tab's visits or jobs, a page at a time. */
export interface BoardLists {
  counts: Record<ListTab, number>;
  tab: ListTab;
  visits: BoardVisit[];
  jobs: ListJob[];
  /** The row to read the tab's next page after; null at its end. */
  next: string | null;
}

/** How many rows of a tab a page reads; the rest come on "Load more" (list caps, BL-110). */
export const LIST_PAGE = 50;

/**
 * A tab's next page added under the rows already shown: each row once (a row that moved between two
 * reads isn't drawn twice), the counts and `next` the newer page's. A page of another tab is dropped.
 */
export function addListPage(had: BoardLists, page: BoardLists): BoardLists {
  if (page.tab !== had.tab) return had;
  const visits = new Set(had.visits.map((v) => v.appointmentId));
  const jobs = new Set(had.jobs.map((j) => j.jobId));
  return {
    ...page,
    visits: [...had.visits, ...page.visits.filter((v) => !visits.has(v.appointmentId))],
    jobs: [...had.jobs, ...page.jobs.filter((j) => !jobs.has(j.jobId))],
  };
}

/** The most rows one dispatch.lists read gives. */
const LIST_READ_MAX = 100;

/**
 * How many rows a tab's read asks for: a page, or, when the board refreshes under a tab "Load more"
 * opened, as many as it shows (up to the most one read gives), so the rows read stay in view.
 */
export const listReadSize = (shown: number) => Math.min(LIST_READ_MAX, Math.max(LIST_PAGE, shown));

/** "50 of 212 shown", beside a tab's "Load more". */
export const listShownText = (l: BoardLists) => shownText(l.visits.length || l.jobs.length, l.counts[l.tab]);

/** One row of a tab: who and when on its line, what it's waiting on beside it. */
export interface ListRow {
  key: string;
  jobId: string;
  jobNumber: string;
  customer: string;
  when: string;
  why: string;
  /** The hold's reason, for the row's lead mark (as the Paused tray's). */
  mark: string | null;
}

/** What a tab with nothing on it says. */
export const LIST_EMPTY: Record<ListTab, string> = {
  missed: 'No visit of an earlier day was missed.',
  onHold: 'Nothing is on hold.',
  inProgress: 'Nobody is on the way or on site right now.',
  future: 'Nothing is booked after today.',
  cancelled: 'Nothing was cancelled in the last 30 days.',
  unscheduled: 'Every open job has a visit booked.',
};

/** A tab's name with its count, as its button says it: "On hold 3". */
export const tabLabel = (tab: ListTab, counts: Partial<Record<ListTab, number>> | null) =>
  `${LIST_TABS.find(([k]) => k === tab)![1]}${counts?.[tab] !== undefined ? ` ${counts[tab]}` : ''}`;

/** "Oct 23" this year; with the year in another. */
const day = (at: string, timeZone: string, today: string) =>
  dateText(localDateOf(at, timeZone), 'UTC', { year: false, now: today });

/** The tab's rows, in the server's order. */
export function listRows(
  l: BoardLists,
  timeZone: string,
  today: string,
  techName: (id: string) => string,
): ListRow[] {
  const crew = (v: BoardVisit) =>
    v.techIds.length ? v.techIds.map(techName).join(', ') : 'No technician yet';
  const visit = (v: BoardVisit, when: string, why: string, mark: string | null = null): ListRow => ({
    key: v.appointmentId,
    jobId: v.jobId,
    jobNumber: v.jobNumber,
    customer: v.customerName,
    when,
    why,
    mark,
  });
  // a visit's day, unless it's today's
  const onDay = (v: BoardVisit) =>
    localDateOf(v.start, timeZone) === today
      ? windowText(v, timeZone)
      : `${dateText(localDateOf(v.start, timeZone), 'UTC', { year: false, now: today, weekday: 'short' })}, ${windowText(v, timeZone)}`;
  switch (l.tab) {
    case 'missed':
      return l.visits.map((v) =>
        visit(
          v,
          onDay(v),
          `${v.status === 'dispatched' ? 'Sent, never reached' : 'Never started'} · ${crew(v)}`,
        ),
      );
    case 'onHold':
      return l.visits.map((v) =>
        visit(
          v,
          pausedSince(v, timeZone, today),
          `${v.pauseReason ? pauseWords(v.pauseReason) : 'No reason given'} · ${crew(v)}`,
          v.pauseReason ?? 'none',
        ),
      );
    case 'inProgress':
      return l.visits.map((v) =>
        visit(v, onDay(v), `${v.status === 'working' ? 'On site' : 'On the way'} · ${crew(v)}`),
      );
    case 'future':
      return l.visits.map((v) => visit(v, onDay(v), crew(v)));
    case 'cancelled':
      return l.jobs.map((j) => ({
        key: j.jobId,
        jobId: j.jobId,
        jobNumber: j.jobNumber,
        customer: j.customerName,
        when: j.cancelledAt ? `Cancelled ${day(j.cancelledAt, timeZone, today)}` : 'Cancelled',
        why: j.cancelReason ?? '',
        mark: null,
      }));
    case 'unscheduled':
      return l.jobs.map((j) => ({
        key: j.jobId,
        jobId: j.jobId,
        jobNumber: j.jobNumber,
        customer: j.customerName,
        when: j.lastVisitAt
          ? `Last visit ${day(j.lastVisitAt, timeZone, today)}`
          : `Booked ${day(j.bookedAt, timeZone, today)}, no visit`,
        why: j.summary,
        mark: null,
      }));
  }
}

/**
 * A board with no technicians yet says where the owner adds them, in the words of the nav they have
 * (QA-767): Blueprint's "Setup › People", Riverstone's own tabs. Never a path the nav has dropped.
 */
export const noTechniciansLine = (blueprint: boolean): string =>
  `No technicians yet. The owner adds them under ${blueprint ? pagePath('settings', 'people') : 'Business → Settings → People'}.`;
